import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexPlotOptions,
  ApexResponsive,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  NgApexchartsModule
} from 'ng-apexcharts';

import {MatCardModule} from '@angular/material/card';
import {WorkflowMetric} from "./workflow-metric.model";


@Component({
  selector: 'app-workflow-report',
  standalone: true,
    imports: [MatCardModule, NgApexchartsModule],
  templateUrl: './workflow-report.component.html'
})
export class WorkflowReportComponent implements OnChanges {
  @Input() metricsData: WorkflowMetric[] = [];

  // --- Summary KPIs ---
  summary = {
    totalCalled: 0,
    totalCompleted: 0,
    totalFailed: 0,
    avgAutomationTime: 0 // in seconds, no decimals
  };

  // --- Timeline Chart (Avg Automation Time Over Time) ---
  timelineSeries: ApexAxisChartSeries = [];
  timelineChart!: ApexChart;
  timelineXAxis!: ApexXAxis;
  timelineStroke!: ApexStroke;
  timelineTooltip!: ApexTooltip;

  // --- Issues Donut Chart (Failures vs. Successful Completions) ---
  issuesSeries: ApexNonAxisChartSeries = [];
  issuesChart!: ApexChart;
  issuesLabels: string[] = [];
  issuesTooltip!: ApexTooltip;

  // --- Usage Stacked Bar Chart (Daily Totals) ---
  usageSeries: ApexAxisChartSeries = [];
  usageChart!: ApexChart;
  usageXAxis!: ApexXAxis;
  usagePlotOptions!: ApexPlotOptions;
  usageDataLabels!: ApexDataLabels;
  usageLegend!: ApexLegend;
  usageResponsive!: ApexResponsive[];

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['metricsData'] && this.metricsData.length) {
      this.computeSummary();
      this.buildTimelineChart();
      this.buildIssuesChart();
      this.buildUsageChart();
    }
  }

  private computeSummary(): void {
    const totals = this.metricsData.reduce(
        (acc, cur) => {
          acc.totalCalled += cur.totalAutomationCalled;
          acc.totalCompleted += cur.totalAutomationCompleted;
          acc.totalFailed += cur.totalAutomationFailed;
          // For avg automation time, weight by totalAutomationCompleted if value present.
          if (cur.totalAutomationCompleted > 0 && cur.avgAutomationTimeTaken !== null) {
            acc.timeSum += cur.avgAutomationTimeTaken * cur.totalAutomationCompleted;
            acc.count += cur.totalAutomationCompleted;
          }
          return acc;
        },
        { totalCalled: 0, totalCompleted: 0, totalFailed: 0, timeSum: 0, count: 0 }
    );

    this.summary.totalCalled = totals.totalCalled;
    this.summary.totalCompleted = totals.totalCompleted;
    this.summary.totalFailed = totals.totalFailed;
    // Convert ms to seconds and round (if no data, show 0).
    this.summary.avgAutomationTime =
        totals.count > 0 ? Math.round(totals.timeSum / totals.count / 1000) : 0;
  }

  private buildTimelineChart(): void {
    // Group metrics by date (YYYY-MM-DD) to compute a weighted average automation time.
    const grouped: { [date: string]: { sum: number; count: number } } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { sum: 0, count: 0 };
      }
      if (metric.totalAutomationCompleted > 0 && metric.avgAutomationTimeTaken !== null) {
        grouped[date].sum += metric.avgAutomationTimeTaken * metric.totalAutomationCompleted;
        grouped[date].count += metric.totalAutomationCompleted;
      }
    });
    const dates = Object.keys(grouped).sort();
    const seriesData = dates.map(date =>
        grouped[date].count > 0 ? Math.round(grouped[date].sum / grouped[date].count / 1000) : 0
    );

    this.timelineSeries = [
      { name: 'Avg Workflow Time (s)', data: seriesData }
    ];
    this.timelineChart = {
      type: 'line',
      height: 350,
      toolbar: { show: true }
    };
    this.timelineXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.timelineStroke = { curve: 'smooth', width: 3 };
    this.timelineTooltip = {
      y: { formatter: (val: number) => `${val} s` }
    };
  }

  private buildIssuesChart(): void {
    // For the issues chart, compare failures vs. successful completions.
    const totalCompleted = this.metricsData.reduce((sum, m) => sum + m.totalAutomationCompleted, 0);
    const totalFailed = this.metricsData.reduce((sum, m) => sum + m.totalAutomationFailed, 0);
    const successful = totalCompleted - totalFailed;

    this.issuesSeries = [totalFailed, successful];
    this.issuesLabels = ['Failed', 'Successful'];
    this.issuesChart = {
      type: 'donut',
      height: 350
    };
    this.issuesTooltip = {
      y: { formatter: (val: number) => val.toString() }
    };
  }

  private buildUsageChart(): void {
    // Group by date to aggregate daily totals.
    const grouped: {
      [date: string]: {
        called: number;
        completed: number;
        failed: number;
      };
    } = {};
    this.metricsData.forEach(metric => {
      const date = metric.reportDate.split('T')[0];
      if (!grouped[date]) {
        grouped[date] = { called: 0, completed: 0, failed: 0 };
      }
      grouped[date].called += metric.totalAutomationCalled;
      grouped[date].completed += metric.totalAutomationCompleted;
      grouped[date].failed += metric.totalAutomationFailed;
    });

    const dates = Object.keys(grouped).sort();
    const calledSeries = dates.map(date => grouped[date].called);
    const completedSeries = dates.map(date => grouped[date].completed);
    const failedSeries = dates.map(date => grouped[date].failed);

    this.usageSeries = [
      { name: 'Called', data: calledSeries },
      { name: 'Completed', data: completedSeries },
      { name: 'Failed', data: failedSeries }
    ];
    this.usageChart = {
      type: 'bar',
      height: 350,
      stacked: true,
      toolbar: { show: true }
    };
    this.usageXAxis = {
      categories: dates,
      title: { text: 'Date' }
    };
    this.usagePlotOptions = {
      bar: { horizontal: false, columnWidth: '35%', borderRadius: 4 }
    };
    this.usageDataLabels = { enabled: false };
    this.usageLegend = { show: true };
    this.usageResponsive = [
      {
        breakpoint: 600,
        options: { plotOptions: { bar: { borderRadius: 3 } } }
      }
    ];
  }
}
